import GeneralAssignedTask from '@/components/deal/tasks/general-assigned-task.tsx';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { PDKNoUserIcon } from '@/components/ui/pdk-icons';
import getCallMeBy from '@/components/user/get-call-me-by';
import { useTasksMutations } from '@/hooks/tasks/use-tasks-mutations.ts';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import useUsers from '@/hooks/use-users';
import { pdkAxios } from '@/lib/pdk-axios';
import { Deal } from '@/models/registry';
import { AssignedTask } from '@/models/registry.ts';
import { assignedTaskStore, dealStore } from '@/models/stores';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import { TaskableType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import compact from 'lodash/compact';
import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import DealAssignedTask from './deal-assigned-task';

const DealAssignedTasks = observer(({ deal }: { deal: Deal }) => {
  const dealId = deal.id;
  const dealShortId = deal.short_id;

  const { isFeatureEnabled: isTasksV2Enabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.GENERALIZED_TASKS,
  });

  const assignedTasks = !dealId
    ? []
    : isTasksV2Enabled
      ? taskStoreV2.getByTarget(dealId, TaskableType.Deal)
      : assignedTaskStore.storeIndexedByForeignKey.get(dealId) || [];

  const {
    assignUserToTaskMutation: assignUserToTaskMutationV2,
    setTaskToDoneMutation: setTaskToDoneMutationV2,
    setTaskToIncompleteMutation: setTaskToIncompleteMutationV2,
    unassignTaskMutation: unassignTaskMutationV2,
  } = useTasksMutations({
    taskableType: TaskableType.Deal,
    taskableId: dealId ?? '',
  });

  const { users } = useUsers();

  const queryClient = useQueryClient();

  const userOptions = useMemo(() => {
    return [
      {
        value: 'unassigned',
        label: 'No User',
        icon: <PDKNoUserIcon className="size-3.5 rounded-full" />,
        searchTerms: ['No User'],
      },
      ...users.map((user) => {
        const user_full_name = `${user.first_name} ${user.last_name}`.trim();

        return {
          value: user.id,
          label: getCallMeBy(user),
          icon: (
            <PaddockAvatar
              src={user.avatar}
              alt={getCallMeBy(user)}
              fallback={`${user.first_name.charAt(0)} ${user.last_name.charAt(0)}`}
              className="w-3.5 h-3.5 rounded-full"
            />
          ),
          searchTerms: compact([getCallMeBy(user), user_full_name]),
        };
      }),
    ];
  }, [users]);

  const assignUserToTaskMutation = useMutation({
    onMutate: async ({
      taskId,
      userId,
    }: {
      taskId: string;
      userId: string | null;
    }) => {
      assignedTaskStore.updateAssignee({
        id: taskId,
        assignee_id: userId,
      });
    },
    mutationFn: async ({
      taskId,
      userId,
    }: {
      taskId: string;
      userId: string | null;
    }) => {
      const url = userId
        ? `/deals/${dealId}/tasks/assign/${taskId}/assign/${userId}`
        : `/deals/${dealId}/tasks/assign/${taskId}/unassign`;

      const response = await pdkAxios.post(url).catch((e) => {
        console.error('Error assigning/unassigning user to task', e);
        throw e;
      });

      return response.data;
    },
    onError: (error, variables) => {
      console.error('Failed to assign/unassign user to task', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const setTaskToDoneMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      assignedTaskStore.updateStatus({
        id: taskId,
        status: 'done',
      });
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/tasks/${taskId}/done`)
        .catch((e) => {
          console.error('Error setting task to done', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      // TODO: update the task to done
    },
    onError: (error, variables) => {
      console.error('Failed to set task to done', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const unassignTaskMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      if (!dealId) return;

      dealStore.removeAssignedTask({ id: dealId, taskId });
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/tasks/${taskId}/unassign`)
        .catch((e) => {
          console.error('Error unassigning task', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      // TODO: update the task to done
    },
    onError: (error, variables) => {
      console.error('Failed to unassign task', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });
  const setTaskToIncompleteMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      assignedTaskStore.updateStatus({
        id: taskId,
        status: 'incomplete',
      });
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/tasks/${taskId}/incomplete`)
        .catch((e) => {
          console.error('Error setting task to incomplete', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      // TODO: update the task to done
    },
    onError: (error, variables) => {
      console.error('Failed to set task to incomplete', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const setTaskToDone = (taskId: string) => {
    setTaskToDoneMutation.mutate({
      taskId,
    });
    setTaskToDoneMutationV2.mutate({
      taskId,
    });
  };

  const setTaskToIncomplete = (taskId: string) => {
    setTaskToIncompleteMutation.mutate({
      taskId,
    });
    setTaskToIncompleteMutationV2.mutate({
      taskId,
    });
  };

  const unassignTask = (taskId: string) => {
    unassignTaskMutation.mutate({
      taskId,
    });
    unassignTaskMutationV2.mutate({
      taskId,
    });
  };

  const handleUserAssignment = useCallback(
    (taskId: string, userId: string) => {
      const actualUserId = userId === 'unassigned' ? null : userId;
      assignUserToTaskMutation.mutate({
        taskId,
        userId: actualUserId,
      });
      assignUserToTaskMutationV2.mutate({
        taskId,
        userId: actualUserId,
      });
    },
    [assignUserToTaskMutation, assignUserToTaskMutationV2],
  );

  return (
    <div className="flex flex-col gap-1 px-2">
      <div>
        {assignedTasks.map((task, index) => {
          const assignedUser = task.assignee_id
            ? users.find((u) => u.id === task.assignee_id)
            : null;

          return task instanceof AssignedTask ? (
            <DealAssignedTask
              key={task.id + index}
              task={task}
              index={index}
              setTaskToDone={setTaskToDone}
              setTaskToIncomplete={setTaskToIncomplete}
              handleUserAssignment={handleUserAssignment}
              userOptions={userOptions}
              assignedUser={assignedUser}
              unassignTask={unassignTask}
            />
          ) : (
            <GeneralAssignedTask
              key={task.id + index}
              task={task}
              index={index}
              setTaskToDone={setTaskToDone}
              setTaskToIncomplete={setTaskToIncomplete}
              handleUserAssignment={handleUserAssignment}
              userOptions={userOptions}
              assignedUser={assignedUser}
              unassignTask={unassignTask}
            />
          );
        })}
      </div>
    </div>
  );
});

export default DealAssignedTasks;
